import { Component, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { OmsService } from '../../services/oms.service';
import { WarehouseService } from '../warehouse.service';

/**
 * Warehouse pick board — the wall display.
 *
 * Read-only view of the same pickingfreeze work the pick station and the legacy
 * order-pick screen run on. It answers three questions from across the room:
 * what is next, how long until the truck leaves, and is anything late or short.
 * Refreshes on a timer and on the socket's orderitempick push, so a line picked
 * on an iPad moves on the wall a second later.
 */
@Component({
	selector: 'app-pick-board',
	templateUrl: './pick-board.component.html',
	styleUrls: ['./pick-board.component.scss'],
})
export class PickBoardComponent implements OnInit, OnDestroy {

	@ViewChild('boardRoot') boardRoot: ElementRef;

	user: any = false;
	location = '';
	locationName = '';

	loading = true;
	error = '';

	orders: any[] = [];
	ready: any[] = [];
	cutoffs: any[] = [];
	stats: any = {
		orders: 0, to_pick: 0, in_progress: 0, ready: 0,
		short: 0, late: 0, due_today: 0, lines: 0, lines_done: 0,
		units: 0, units_picked: 0, held: 0, pickers: 0,
	};

	/** Same tenant asset config the station reads, so the wall groups identically. */
	groupBy = '';

	now = new Date();
	lastUpdated: Date = null;
	live = false;
	fullscreen = false;

	/**
	 * This screen's own config, in this browser's localStorage.
	 *
	 * A TV bolted to the parcel lane wants the parcel lane forever, so the filter
	 * belongs with the screen rather than in shared server config — set it once on
	 * the TV and it survives reboots. Two screens on the same login can therefore
	 * show completely different work, which is the point.
	 */
	config: any = {
		name: '',
		/**
		 * Warehouses this screen watches. Empty = all of them. Multi so one screen
		 * can cover several sites — a supervisor shouldn't need a TV per warehouse.
		 */
		loccodes: [],
		stage: 'pick',
		shipvia: [],
		area: [],
		deliveryroute: [],
		bins: [],
		// Typed patterns like `RM*` or `B%`, so a room can be covered without
		// ticking every bin in it.
		binPattern: '',
		zip: '',
		line: '',
		pageSize: 7,
		rotateSec: 14,
		showRail: true,
		/**
		 * 'dark' suits a dim warehouse; 'light' is for a bright or window-facing
		 * screen where dark-on-glare is unreadable. Per screen, like everything
		 * else here — two boards in one building can legitimately differ.
		 */
		theme: 'dark',
	};

	settingsOpen = false;
	options: any = { shipvia: [], bins: [], areas: [], routes: [], locations: [] };
	optionsLoading = false;

	/**
	 * Cutoffs being edited. Unlike everything else in this panel these are NOT
	 * per-screen: they're server config, so a change here moves the deadline on
	 * every board in the building. That's the point — two TVs disagreeing about
	 * when the truck leaves is worse than no countdown at all — but it's why they
	 * sit in their own block with their own save button, well away from the
	 * "Save to this screen" footer.
	 */
	cutoffEdit: any[] = [];

	/** True while the times on the wall are placeholders nobody chose. */
	cutoffsAreDefault = false;
	cutoffMax = 6;
	cutoffSaving = false;
	cutoffSaved = false;
	cutoffError = '';

	/**
	 * Set the moment a cutoff is touched. The footer's "Save to this screen" does
	 * NOT write these — different scope — so an edited-but-unsaved cutoff has to
	 * announce itself, or someone types the new truck time, saves the screen, and
	 * walks away believing the building knows.
	 */
	cutoffDirty = false;

	/** What the server confirms it filtered on — printed on the wall. */
	activeFilters: any = {};

	/** What the server dropped to keep the payload sane, so the wall can say it. */
	truncated: any = null;

	/**
	 * The staged rail renders every row it is given. On a tenant whose
	 * pickingfreeze was never cleaned that is thousands of DOM nodes, which is
	 * what made the board crawl. Show a handful and count the rest.
	 */
	readonly railLimit = 40;

	// The board pages itself so row 14 gets its turn on the wall.
	page = 0;
	get pageSize(): number {
		return Math.max(3, +this.config.pageSize || 7);
	}
	// With a healthy socket the push does the work and this is just a safety net.
	// Without one it is the only thing keeping the wall honest, so it runs tighter.
	readonly refreshMs = 20000;
	readonly refreshMsOffline = 8000;
	readonly pageMs = 14000;

	private clockTimer: any = null;
	private refreshTimer: any = null;
	private claimTimer: any = null;
	private pageTimer: any = null;
	private pushTimer: any = null;
	private healthTimer: any = null;
	private offlineTimer: any = null;
	private userSub: any = null;
	private inflight: any = null;

	constructor(
		public router: Router,
		private warehouseService: WarehouseService,
		private omsService: OmsService,
		private globalSearchService: GlobalSearchService,
	) {}

	ngOnInit(): void {
		// Config and grouping FIRST. `globalSearchService.user` is a BehaviorSubject,
		// so subscribing fires synchronously — do it before this and the very first
		// request goes out with an empty config: the board fetches the whole
		// warehouse while the banner already claims to be filtered, and stays that
		// way until the refresh timer. A wall that misreports its own scope is worse
		// than one that's slow.
		this.loadConfig();

		const cfg = this.globalSearchService.configsubscription.value;
		const by = `${(cfg && cfg.picking && cfg.picking.groupBy) || ''}`.toLowerCase();
		this.groupBy = ['bin', 'productline', 'brand'].indexOf(by) > -1 ? by : '';

		this.userSub = this.globalSearchService.user.subscribe((u: any) => {
			if (!u) { return; }
			this.user = u;
			const loc = u.user && u.user.defaultlocation ? u.user.defaultlocation.loccode : '';
			// The screen's own setting wins — a board can watch a location the person
			// who set it up doesn't personally default to.
			// The screen's own selection wins. Nothing selected means every
			// warehouse, not the viewer's default — a wall board is not personal.
			const wanted = (this.config.loccodes && this.config.loccodes.length)
				? this.config.loccodes.join(',')
				: '';
			if (wanted !== this.location) {
				this.location = wanted;
			}
			if (!this.savedConfigFound && this.location === '' && !this.config.loccodes.length && loc) {
				// A genuinely fresh screen: start where the person setting it up works,
				// then they can widen it. Only when nothing was ever saved — a screen
				// saved as "All warehouses" has an empty list on purpose, and this used
				// to overwrite that with the viewer's own site on every refresh, which
				// looked exactly like the saved settings being thrown away.
				this.config.loccodes = [loc];
				this.location = loc;
			}
			this.connect();
			this.load();
		});

		this.clockTimer = setInterval(() => { this.now = new Date(); }, 1000);
		this.refreshTimer = setInterval(() => this.load(true), this.refreshMs);
		this.offlineTimer = setInterval(() => {
			if (!this.live) { this.load(true); }
		}, this.refreshMsOffline);
		this.startPageTimer();

		document.addEventListener('fullscreenchange', this.onFsChange);
	}

	ngOnDestroy(): void {
		[this.clockTimer, this.refreshTimer, this.pageTimer, this.pushTimer,
			this.healthTimer, this.offlineTimer, this.previewTimer, this.claimTimer].forEach(t => {
			if (t) { clearInterval(t); clearTimeout(t); }
		});
		if (this.userSub) { this.userSub.unsubscribe(); }
		if (this.inflight) { this.inflight.unsubscribe(); }
		document.removeEventListener('fullscreenchange', this.onFsChange);
	}

	// ── Data ─────────────────────────────────────────────────────────────────

	load(silent = false) {
		if (!silent) { this.loading = true; }
		if (this.inflight) { this.inflight.unsubscribe(); }

		this.inflight = this.warehouseService.getPickBoard(this.location, this.config.stage, this.groupBy, this.outboundFilters()).subscribe({
			next: (r: any) => {
				this.loading = false;
				this.inflight = null;
				if (!r || r.success !== true) {
					this.error = (r && r.message) ? r.message : 'Pick board did not load.';
					return;
				}
				this.error = '';
				this.locationName = r.location_name || '';
				this.scopeLocations = r.locations || [];
				this.multiLocation = !!r.multi_location;
				this.cutoffs = r.cutoffs || [];
				// The server flags placeholder cutoffs so the wall can mark them as
				// examples. A made-up deadline shown as real has the floor racing a
				// truck that isn't coming.
				this.cutoffsAreDefault = this.cutoffs.length > 0 && !!this.cutoffs[0].is_default;
				this.stats = r.stats || this.stats;
				const all = r.orders || [];
				this.orders = all.filter((o: any) => o.bucket !== 'ready');
				this.ready = all.filter((o: any) => o.bucket === 'ready');
				this.truncated = r.truncated || null;
				this.activeFilters = r.filters || {};
				this.lastUpdated = new Date();
				if (this.page > this.pageCount - 1) { this.page = 0; }
			},
			error: () => {
				this.loading = false;
				this.inflight = null;
				this.error = 'Pick board is not reachable.';
			},
		});
	}

	/**
	 * The socket fires once per scanned line; a busy floor would otherwise
	 * hammer the rollup query. Coalesce into one reload a second later.
	 */
	private connect() {
		const token = this.user && this.user.user ? this.user.user.userid : '';
		if (!token) { return; }

		this.omsService.setupSocketConnection(token);
		this.live = this.omsService.isSocketHealthy();

		// The dot said "live" the moment we asked for a socket, whether or not one
		// ever connected — so a dead socket looked healthy while the board quietly
		// fell back to its 20s poll. Track the real state.
		if (!this.healthTimer) {
			this.healthTimer = setInterval(() => {
				this.live = this.omsService.isSocketHealthy();
			}, 5000);
		}

		this.omsService.subscribeToOrderPicks((_err: any, _msg: any) => {
			this.live = true;
			if (this.pushTimer) { clearTimeout(this.pushTimer); }
			this.pushTimer = setTimeout(() => this.load(true), 1200);
		});

		// Claiming, starting and completing are plain HTTP writes with no count
		// attached, so they never came through the pick echo above — the wall only
		// caught up on its 20s poll. They arrive singly rather than in a scan
		// burst, so this refetches almost straight away instead of waiting out the
		// 1.2s the count echo needs to coalesce.
		this.omsService.subscribeToPickActivity((_err: any, _msg: any) => {
			this.live = true;
			if (this.claimTimer) { clearTimeout(this.claimTimer); }
			this.claimTimer = setTimeout(() => this.load(true), 250);
		});
	}

	// ── Per-screen config ────────────────────────────────────────────────────

	private readonly CONFIG_KEY = 'pickBoardConfig';

	/** True once a saved config was read back — an empty selection is then deliberate. */
	private savedConfigFound = false;

	private loadConfig() {
		try {
			const raw = localStorage.getItem(this.CONFIG_KEY);
			if (raw) {
				const saved = JSON.parse(raw);
				this.savedConfigFound = true;
				// Screens saved before multi-warehouse held a single `loccode`.
				if (saved.loccode !== undefined && saved.loccodes === undefined) {
					saved.loccodes = `${saved.loccode}`.trim() === '' ? [] : [saved.loccode];
					delete saved.loccode;
				}
				// Merged over the defaults so a config written by an older build
				// doesn't leave new keys undefined.
				this.config = { ...this.config, ...saved };
			}
		} catch {
			// Private window or blocked storage: run on defaults, unfiltered.
		}
	}

	/**
	 * What actually goes to the server: the ticked bins plus anything typed as a
	 * pattern. Kept separate in the config so clearing one doesn't wipe the other.
	 */
	private outboundFilters(): any {
		const bins = [...(this.config.bins || [])];
		for (const piece of `${this.config.binPattern || ''}`.split(',')) {
			const v = piece.trim();
			if (v !== '') { bins.push(v); }
		}
		return { ...this.config, bins };
	}

	private saveConfig() {
		try {
			localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
		} catch {
			// Nothing to do — the board still works, it just won't remember.
		}
	}

	get isLight(): boolean {
		return this.config.theme === 'light';
	}

	/** Quick flip from the board chrome — no need to open settings for this one. */
	toggleTheme(event?: any) {
		if (event) { event.stopPropagation(); }
		this.config.theme = this.isLight ? 'dark' : 'light';
		this.saveConfig();
	}

	openSettings(event?: any) {
		if (event) { event.stopPropagation(); }
		this.settingsOpen = true;
		this.loadFilterOptions();
		this.loadCutoffEditor();
		this.refreshPreview();
	}

	closeSettings() {
		this.settingsOpen = false;
	}

	// ── Cutoff times (shared by every board) ─────────────────────────────────

	private loadCutoffEditor() {
		this.cutoffError = '';
		this.cutoffSaved = false;
		this.cutoffDirty = false;
		this.warehouseService.getPickCutoffs().subscribe({
			next: (r: any) => {
				if (!r || !r.success) { return; }
				this.cutoffsAreDefault = !!r.is_default;
				this.cutoffMax = r.max || 6;
				// Copied, not aliased: editing must not move the wall until saved.
				this.cutoffEdit = (r.cutoffs || []).map((c: any) => ({
					label: c.label || '',
					time: c.time || '',
					loccode: c.loccode || '',
					shipvia: (c.shipvia || []).map((v: any) => +v),
				}));
			},
			error: () => { this.cutoffError = 'Could not read the cutoff times.'; },
		});
	}

	/** Any edit inside the block — typed, picked or toggled. */
	cutoffTouched() {
		this.cutoffDirty = true;
		this.cutoffSaved = false;
	}

	addCutoff() {
		if (this.cutoffEdit.length >= this.cutoffMax) { return; }
		this.cutoffEdit.push({ label: '', time: '', loccode: '', shipvia: [] });
		this.cutoffTouched();
	}

	removeCutoff(i: number) {
		this.cutoffEdit.splice(i, 1);
		this.cutoffTouched();
	}

	isCutoffShipvia(c: any, id: any): boolean {
		return (c.shipvia || []).indexOf(+id) > -1;
	}

	toggleCutoffShipvia(c: any, id: any) {
		const list = Array.isArray(c.shipvia) ? c.shipvia : [];
		const i = list.indexOf(+id);
		if (i > -1) { list.splice(i, 1); } else { list.push(+id); }
		c.shipvia = list;
		this.cutoffTouched();
	}

	/** A cutoff with no ship via is the catch-all — everything else lands on it. */
	cutoffScope(c: any): string {
		const names = (c.shipvia || [])
			.map((id: any) => {
				const m = (this.options.shipvia || []).find((s: any) => +s.id === +id);
				return m ? m.name : `Ship via ${id}`;
			});
		return names.length ? names.join(', ') : 'Everything not claimed by another cutoff';
	}

	/**
	 * Writes the shared set. The server validates and refuses the whole save with
	 * a plain reason rather than dropping the row someone typed, so anything it
	 * rejects is shown here verbatim.
	 */
	saveCutoffs() {
		this.cutoffSaving = true;
		this.cutoffError = '';
		this.cutoffSaved = false;
		this.warehouseService.savePickCutoffs(this.cutoffEdit).subscribe({
			next: (r: any) => {
				this.cutoffSaving = false;
				if (!r || !r.success) {
					this.cutoffError = (r && r.message) ? r.message : 'The cutoff times were not saved.';
					return;
				}
				this.cutoffsAreDefault = false;
				this.cutoffSaved = true;
				this.cutoffDirty = false;
				this.cutoffEdit = (r.cutoffs || []).map((c: any) => ({
					label: c.label || '',
					time: c.time || '',
					loccode: c.loccode || '',
					shipvia: (c.shipvia || []).map((v: any) => +v),
				}));
				// Pull the board so the marquee and the per-order countdowns pick up
				// the new deadlines immediately instead of at the next poll.
				this.load(true);
			},
			error: () => {
				this.cutoffSaving = false;
				this.cutoffError = 'The cutoff times were not saved.';
			},
		});
	}

	/** Only offers filters that would actually match open work right now. */
	private loadFilterOptions() {
		this.optionsLoading = true;
		this.warehouseService.getPickBoardFilterOptions(this.location).subscribe({
			next: (r: any) => {
				this.optionsLoading = false;
				if (r && r.success) {
					this.options = {
						shipvia: r.shipvia || [],
						bins: r.bins || [],
						areas: r.areas || [],
						routes: r.routes || [],
						locations: r.locations || [],
					};
				}
			},
			error: () => { this.optionsLoading = false; },
		});
	}

	/** Multi-select chips write straight into the config array. */
	toggleFilter(key: string, value: any) {
		const list = Array.isArray(this.config[key]) ? this.config[key] : [];
		const v = `${value}`;
		const i = list.indexOf(v);
		if (i > -1) { list.splice(i, 1); } else { list.push(v); }
		this.config[key] = list;
		this.refreshPreview();
	}

	isFilterOn(key: string, value: any): boolean {
		const list = Array.isArray(this.config[key]) ? this.config[key] : [];
		return list.indexOf(`${value}`) > -1;
	}

	/**
	 * Room shortcut: a bin's leading letters are the room, so `B*` selects the lot
	 * without ticking twenty-nine individual bins.
	 */
	get binRooms(): string[] {
		const rooms = new Set<string>();
		for (const b of this.options.bins || []) {
			const name = `${b.name || ''}`.trim();
			if (name.length) { rooms.add(name.charAt(0).toUpperCase()); }
		}
		return Array.from(rooms).sort();
	}

	toggleRoom(letter: string) {
		this.toggleFilter('bins', letter + '*');
	}

	isRoomOn(letter: string): boolean {
		return this.isFilterOn('bins', letter + '*');
	}

	applySettings() {
		this.saveConfig();
		this.settingsOpen = false;
		this.page = 0;
		// From loccodes, never the legacy single `loccode`: the migration deletes
		// that key, so reading it pinned the request to the previously applied
		// location while the panel showed both warehouses selected.
		this.location = (this.config.loccodes || []).join(',');
		this.startPageTimer();
		this.load();
	}

	/**
	 * Clears and applies in one go. It previously only reset the pending config,
	 * so the board carried on showing the old filter and the button looked broken.
	 */
	clearFilters() {
		this.config = {
			...this.config,
			shipvia: [], area: [], deliveryroute: [], bins: [],
			binPattern: '', zip: '', line: '',
		};
		this.saveConfig();
		this.activeFilters = {};
		this.previewMatches = null;
		this.load();
		this.refreshPreview();
	}

	/** Everything currently narrowing this screen, as removable chips. */
	get selectedFilters(): any[] {
		const out: any[] = [];
		const push = (key: string, label: string, value: string, shown: string) =>
			out.push({ key, label, value, shown });

		for (const v of this.config.shipvia || []) {
			const hit = (this.options.shipvia || []).find((o: any) => `${o.id}` === `${v}`);
			push('shipvia', 'Ship via', v, hit ? hit.name : v);
		}
		for (const v of this.config.area || []) {
			const hit = (this.options.areas || []).find((o: any) => `${o.code}` === `${v}`);
			push('area', 'Area', v, hit ? hit.name : v);
		}
		for (const v of this.config.deliveryroute || []) {
			const hit = (this.options.routes || []).find((o: any) => `${o.code}` === `${v}`);
			push('deliveryroute', 'Route', v, hit ? hit.name : v);
		}
		for (const v of this.config.bins || []) {
			push('bins', 'Bin', v, v);
		}
		return out;
	}

	removeSelected(f: any) {
		this.toggleFilter(f.key, f.value);
		this.refreshPreview();
	}

	get hasPendingFilters(): boolean {
		return this.selectedFilters.length > 0
			|| `${this.config.binPattern || ''}`.trim() !== ''
			|| `${this.config.zip || ''}`.trim() !== ''
			|| `${this.config.line || ''}`.trim() !== '';
	}

	// ── Live match count ─────────────────────────────────────────────────────

	previewMatches: number = null;
	previewing = false;
	private previewTimer: any = null;

	/**
	 * Debounced so typing a zip doesn't fire a request per keystroke.
	 */
	refreshPreview() {
		if (this.previewTimer) { clearTimeout(this.previewTimer); }
		this.previewing = true;
		this.previewTimer = setTimeout(() => {
			const loc = (this.config.loccodes || []).join(',') || this.location;
			this.warehouseService
				.previewPickBoard(loc, this.config.stage, this.groupBy, this.outboundFilters())
				.subscribe({
					next: (r: any) => {
						this.previewing = false;
						this.previewMatches = (r && r.truncated)
							? (r.truncated.queue_total + r.truncated.ready_total)
							: null;
					},
					error: () => { this.previewing = false; this.previewMatches = null; },
				});
		}, 450);
	}

	/** Rotation speed is per-screen too: a short list doesn't need to cycle fast. */
	private startPageTimer() {
		if (this.pageTimer) { clearInterval(this.pageTimer); }
		const secs = Math.max(4, +this.config.rotateSec || 14);
		this.pageTimer = setInterval(() => this.nextPage(), secs * 1000);
	}

	/**
	 * The wall must always say what it is showing. A filtered board that looks
	 * unfiltered reads as "the warehouse is quiet" when it is anything but.
	 */
	/**
	 * Read straight off what the server confirmed it filtered on, with the names
	 * it resolved — so the banner says "Ship via: UPS Ground" on a TV that has
	 * been power-cycled and never had its settings panel opened.
	 *
	 * Falls back to the local config only until the first response lands, so the
	 * banner is never blank while a filter is in force.
	 */
	get filterSummary(): string[] {
		const confirmed = this.activeFilters || {};
		const keys = Object.keys(confirmed);
		if (keys.length) {
			// Two server shapes are in the wild: the current one sends
			// {label, value, shown} with names already resolved; an older build sent
			// the raw string. Assuming the new shape printed "undefined: undefined"
			// on a board pointed at an older server, so handle both.
			// Whitelist, not passthrough. Anything the wall doesn't recognise is
			// dropped rather than printed: rendering unknown keys put
			// "route: apir/warehouse/pickboard/board" on a warehouse TV, because
			// OpenCart's own ?route= param was being echoed back as a filter.
			// Nothing reaches the wall unless it is a filter a picker would recognise.
			const known: any = {
				shipvia: 'Ship via',
				area: 'Area',
				deliveryroute: 'Route',
				zip: 'Zip',
				bins: 'Bins',
				line: 'Line',
			};
			return keys
				.filter(k => known[k])
				.map(k => {
					const v = confirmed[k];
					const raw = (v && typeof v === 'object')
						? ((v.shown !== undefined && v.shown !== null) ? v.shown : v.value)
						: v;
					const shown = `${raw ?? ''}`.trim();
					const label = (v && typeof v === 'object' && v.label) ? v.label : known[k];
					return shown === '' ? '' : `${label}: ${shown}`;
				})
				.filter(t => t !== '');
		}

		const out: string[] = [];
		const show = (label: string, v: any) => {
			const val = Array.isArray(v) ? v.join(', ') : `${v || ''}`.trim();
			if (val !== '') { out.push(`${label}: ${val}`); }
		};
		show('Ship via', this.config.shipvia);
		show('Area', this.config.area);
		show('Route', this.config.deliveryroute);
		show('Bins', this.config.bins);
		show('Zip', this.config.zip);
		show('Line', this.config.line);
		return out;
	}

	get isFiltered(): boolean {
		return this.filterSummary.length > 0;
	}

	/** Names of the sites in scope, as the server resolved them. */
	scopeLocations: any[] = [];
	multiLocation = false;

	/** Show the warehouse on each row only when more than one is in scope. */
	get showRowLocation(): boolean {
		return this.multiLocation;
	}

	get scopeLabel(): string {
		if (!this.scopeLocations.length) { return ''; }
		if (this.scopeLocations.length <= 3) {
			return this.scopeLocations.map(l => l.name).join(' · ');
		}
		return `${this.scopeLocations.length} warehouses`;
	}

	get boardTitle(): string {
		return `${this.config.name || ''}`.trim() || this.locationName || 'Warehouse';
	}

	// ── Paging ───────────────────────────────────────────────────────────────

	get pageCount(): number {
		return Math.max(1, Math.ceil(this.orders.length / this.pageSize));
	}

	get pagedOrders(): any[] {
		const start = this.page * this.pageSize;
		return this.orders.slice(start, start + this.pageSize);
	}

	nextPage() {
		if (this.pageCount <= 1) { this.page = 0; return; }
		this.page = (this.page + 1) % this.pageCount;
	}

	rankOf(index: number): number {
		return this.page * this.pageSize + index + 1;
	}

	// ── Cutoffs & clock ──────────────────────────────────────────────────────

	/** The next deadline that hasn't passed — the one the countdown shows. */
	get nextCutoff(): any {
		for (const c of this.cutoffs) {
			if (this.minutesLeft(c) > 0) { return c; }
		}
		return null;
	}

	/** Recomputed from the wall clock so the countdown ticks between refreshes. */
	minutesLeft(cutoff: any): number {
		if (!cutoff || !cutoff.at) { return 0; }
		const at = new Date(cutoff.at).getTime();
		return Math.floor((at - this.now.getTime()) / 60000);
	}

	countdown(cutoff: any): string {
		const m = this.minutesLeft(cutoff);
		if (m <= 0) { return 'CLOSED'; }
		const h = Math.floor(m / 60);
		const mm = m % 60;
		return h > 0 ? `${h}h ${String(mm).padStart(2, '0')}m` : `${mm}m`;
	}

	cutoffTone(cutoff: any): string {
		const m = this.minutesLeft(cutoff);
		if (m <= 0) { return 'closed'; }
		if (m <= 30) { return 'critical'; }
		if (m <= 90) { return 'warn'; }
		return 'ok';
	}

	/** Per-order urgency, which is what actually colours the row. */
	rowTone(o: any): string {
		if (o.late) { return 'critical'; }
		if (o.short_lines > 0) { return 'short'; }
		const m = o.minutes_to_cutoff;
		if (m !== null && m !== undefined) {
			const live = this.minutesFromNow(o);
			if (live <= 0) { return 'closed'; }
			if (live <= 30) { return 'critical'; }
			if (live <= 90) { return 'warn'; }
		}
		if (o.asap) { return 'warn'; }
		return 'ok';
	}

	minutesFromNow(o: any): number {
		const idx = o.cutoff_index;
		if (idx === null || idx === undefined || !this.cutoffs[idx]) { return 9999; }
		return this.minutesLeft(this.cutoffs[idx]);
	}

	orderCountdown(o: any): string {
		const idx = o.cutoff_index;
		if (idx === null || idx === undefined || !this.cutoffs[idx]) { return '—'; }
		return this.countdown(this.cutoffs[idx]);
	}

	age(o: any): string {
		const m = o.age_minutes || 0;
		if (m < 60) { return `${m}m`; }
		const h = Math.floor(m / 60);
		if (h < 48) { return `${h}h`; }
		return `${Math.floor(h / 24)}d`;
	}

	// ── Chrome ───────────────────────────────────────────────────────────────

	private onFsChange = () => {
		this.fullscreen = !!document.fullscreenElement;
	};

	toggleFullscreen() {
		const el = this.boardRoot ? this.boardRoot.nativeElement : null;
		if (!el) { return; }
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else if (el.requestFullscreen) {
			el.requestFullscreen();
		}
	}

	openOrder(o: any) {
		this.router.navigate(['/warehouse/pick-station', o.orderno]);
	}

	get railOrders(): any[] {
		return this.config.showRail === false ? [] : this.ready.slice(0, this.railLimit);
	}

	get railHidden(): number {
		const total = this.truncated ? this.truncated.ready_total : this.ready.length;
		return Math.max(0, total - this.railOrders.length);
	}

	/** Orders the server never sent because the cap kicked in. */
	get queueHidden(): number {
		if (!this.truncated) { return 0; }
		return Math.max(0, this.truncated.queue_total - this.truncated.queue_shown);
	}

	trackByOrderno(_i: number, o: any) {
		return o.orderno;
	}
}
